import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
              <span className="font-heading text-2xl font-bold text-primary-500">
                韩漫
              </span>
              <span className="font-heading text-lg text-foreground-700 hidden sm:block">
                免费韩漫大全
              </span>
            </Link>

            <div className="hidden lg:flex items-center gap-1">
              <Link
                to="/"
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                  location.pathname === '/'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50'
                }`}
              >
                首页
              </Link>
              <div className="relative group">
                <button className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-full transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer">
                  分类
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </button>
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="bg-background-50 rounded-lg p-2 shadow-lg border border-background-200 min-w-[180px]">
                    {categories
                      .filter((c) => c.slug !== 'all')
                      .map((cat) => (
                        <Link
                          key={cat.slug}
                          to={`/category/${cat.slug}`}
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className={`${cat.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                          {cat.name}
                        </Link>
                      ))}
                  </div>
                </div>
              </div>
              <Link
                to="/category/all"
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                  location.pathname.startsWith('/category')
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50'
                }`}
              >
                全部作品
              </Link>
              <Link
                to="/about"
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                  location.pathname === '/about'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50'
                }`}
              >
                关于我们
              </Link>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="搜索漫画..."
                  className="w-48 pl-9 pr-4 py-2 text-sm bg-background-100 border border-background-200 rounded-full text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                />
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm w-4 h-4 flex items-center justify-center"></i>
              </div>
            </div>

            <button
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg text-foreground-700 hover:bg-background-100 cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="菜单"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl w-5 h-5 flex items-center justify-center`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="absolute top-16 left-4 right-4 bg-background-50 rounded-2xl shadow-lg border border-background-200 p-4 animate-in">
            <div className="mb-4">
              <input
                type="text"
                placeholder="搜索漫画..."
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-full text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm w-4 h-4 flex items-center justify-center"></i>
            </div>
            <div className="space-y-1">
              <Link
                to="/"
                className="flex items-center px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-home-line mr-3 text-base w-5 h-5 flex items-center justify-center"></i>首页
              </Link>
              <Link
                to="/category/all"
                className="flex items-center px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-apps-line mr-3 text-base w-5 h-5 flex items-center justify-center"></i>全部作品
              </Link>
              <div className="border-t border-background-200 my-2"></div>
              {categories
                .filter((c) => c.slug !== 'all')
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
                  >
                    <i className={`${cat.icon} mr-3 text-base w-5 h-5 flex items-center justify-center`}></i>
                    {cat.name}
                  </Link>
                ))}
              <div className="border-t border-background-200 my-2"></div>
              <Link
                to="/about"
                className="flex items-center px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-information-line mr-3 text-base w-5 h-5 flex items-center justify-center"></i>关于我们
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}